import { useState } from "react";
import { Layout } from "../../components/Layout";
import { PageHeader } from "../../components/PageHeader";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { useResourceCap } from "../../hooks/useResourceCap";
import { CapUsageBadge } from "../../components/CapUsageBadge";
import { CapAwareButton } from "../../components/CapAwareButton";

import { Input } from "../../components/ui/input";
import { BottomSheet } from "../../components/ui/bottom-sheet";
import { Label } from "../../components/ui/label";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "../../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../../components/ui/alert-dialog";
import { useLang } from "../../contexts/LangContext";
import { tx } from "../../i18n";
import { useProfile, useCashAccounts, useFxRates, useFxConvert } from "../../hooks/queries";
import { MoneyAmount } from "../../components/MoneyAmount";
import { cashAccountsService } from "../../services/profileService";
import { movementsService } from "../../services/transactionsService";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { formatMoney } from "../../lib/api";
import { toast } from "sonner";
import { Plus, Wallet, Landmark, CreditCard, Trash2, Pencil, Save, Loader2, ArrowRightLeft, ListChecks, PlusCircle } from "lucide-react";
import TransferDialog from "../../components/TransferDialog";
import { QuickMovementDialog } from "../../components/QuickMovementDialog";
import { useNavigate } from "react-router-dom";
// iter-169zze — Shared form-UX primitives (FormField, SubmitButton,
// useFormUX) for consistent inline errors, a11y, mobile keyboards
// and submit-locking across the whole app.
import { useFormUX, FormField, SubmitButton } from "../../components/form-ux";

/**
 * Multi-account cash buckets page.
 *
 * Backend CRUD already lives at /api/cash-accounts; this surfaces it so the
 * user can split cash across e.g. Nu / Bancolombia / efectivo and see each
 * balance independently. Dashboard's "current cash" stays as a single
 * primary-account number (backed by the first account); future work can
 * graduate to per-account routing for incomes / expenses.
 */
const TYPE_ICONS = { cash: Wallet, bank: Landmark, wallet: CreditCard };
const TYPE_LABELS = {
  cash: { es: "Efectivo", en: "Cash" },
  bank: { es: "Banco", en: "Bank" },
  wallet: { es: "Billetera", en: "Wallet" },
};

export default function CashAccountsPage() {
  const { lang } = useLang();
  const qc = useQueryClient();
  const { data: profile } = useProfile();
  const { data: accounts = [], isLoading } = useCashAccounts();
  const currency = profile?.currency || "USD";
  const { data: fxInfo } = useFxRates();
  const fxList = Object.keys(fxInfo?.rates || {}).sort();
  const convert = useFxConvert();
  // iter-170o — cap-aware "New account" button.
  const cashCap = useResourceCap("cash_accounts");

  // Compute the per-account display values once. Each row needs the
  // ORIGINAL balance (stored as-is in the account's currency) and the
  // ≈ converted equivalent in the user's profile currency. The
  // originals are NEVER touched — only the display number recomputes
  // when the user flips their profile currency or the FX rate refreshes.
  const accountsWithFx = accounts.map((a) => {
    const accCcy = a.currency || currency;
    const converted = accCcy === currency
      ? Number(a.balance || 0)
      : Number(convert(Number(a.balance || 0), accCcy, currency)) || 0;
    const rate = (accCcy !== currency && Number(a.balance || 0) !== 0)
      ? converted / Number(a.balance)
      : null;
    return { ...a, _converted: converted, _rate: rate, _origCurrency: accCcy };
  });
  const totalCash = accountsWithFx.reduce((sum, a) => sum + a._converted, 0);

  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const navigate = useNavigate();
  // iter-188 — When the user adjusts an account balance manually we
  // pause the save flow and ask whether to record the delta as a
  // movement (Income or Expense). The pending payload + delta sit in
  // this state until the user picks an option in the dialog.
  const [balanceDeltaPrompt, setBalanceDeltaPrompt] = useState(null);
  // ^ shape: { payload, oldBalance, newBalance, delta, ccy, accountId }
  // iter-188 — Quick "+ Movement" shortcut from each account card.
  // Holds the account currently being added against, or null when
  // the dialog is closed.
  const [quickMovementAccount, setQuickMovementAccount] = useState(null);

  // iter-169zze — Centralised form state with inline errors, touch
  // tracking and submit-locking via the shared form-UX primitive.
  const isEs = lang === "es";
  const form = useFormUX(
    { type: "bank", name: "", balance: "", currency: "", institution: "", notes: "" },
    {
      validate: (v) => {
        const fieldErrors = {};
        if (!v.name?.trim()) {
          fieldErrors.name = isEs ? "El nombre es obligatorio." : "Name is required.";
        }
        if (v.balance === "" || v.balance == null) {
          fieldErrors.balance = isEs ? "Indica el saldo actual." : "Enter the current balance.";
        } else if (Number.isNaN(Number(v.balance))) {
          fieldErrors.balance = isEs ? "Saldo inválido." : "Invalid balance.";
        }
        if (v.type !== "cash" && !v.institution?.trim()) {
          // Not strictly required by the backend, but a strong UX hint:
          // an empty bank/wallet name confuses users at month-end review.
        }
        return { fieldErrors };
      },
    },
  );

  // iter-169zze — Old createMutation / updateMutation removed in
  // favour of useFormUX(handleSubmit). The shared primitive owns
  // submitting state + button locking + error mapping.

  const deleteMutation = useMutation({
    mutationFn: (id) => cashAccountsService.remove(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["cash-accounts"] });
      qc.invalidateQueries({ queryKey: ["dashboard"] });
      toast.success(tx(lang, "Account deleted", "Cuenta eliminada", "Cuenta eliminada"));
      setDeleteTarget(null);
    },
    onError: () => toast.error(tx(lang, "Delete failed", "No se pudo eliminar", "No se pudo eliminar")),
  });

  const reset = () => {
    setOpen(false);
    setEditing(null);
    form.reset({ type: "bank", name: "", balance: "", currency: "", institution: "", notes: "" });
  };

  const openEdit = (a) => {
    setEditing(a);
    form.reset({
      type: a.type,
      name: a.name,
      balance: String(a.balance),
      currency: a.currency || "",
      institution: a.institution || "",
      notes: a.notes || "",
    });
    setOpen(true);
  };

  // iter-169zze — Submit handler wrapped through useFormUX so the
  // button auto-disables, errors map to fields, and a network failure
  // surfaces inline + as a toast fallback.
  const onSubmit = form.handleSubmit(async (v) => {
    const payload = {
      type: v.type,
      name: v.name.trim(),
      balance: Number(v.balance),
      currency: v.currency || currency,
      institution: v.type === "cash" ? null : (v.institution.trim() || null),
      notes: v.notes.trim() || "",
    };
    // iter-188 — When the user is editing an EXISTING account and the
    // balance changed, surface a confirmation dialog so they can
    // choose between (a) recording the diff as a real movement
    // (preferred — keeps the audit trail intact and balance ends up
    // correct via the normal movement engine) and (b) a silent
    // adjustment (current legacy behaviour). New accounts always
    // skip the prompt — the starting balance has no diff to record.
    //
    // iter-188-fix-1 — Closing the BottomSheet (`setOpen(false)`)
    // CANNOT use the default ``onOpenChange`` path because that path
    // also calls ``reset()`` and clears ``editing``. We need
    // ``editing`` to survive into the delta dialog handlers. Pattern:
    //   1. close the BottomSheet directly via setOpen(false)
    //   2. stash the prompt payload (which includes the editing id)
    //   3. the delta-dialog handlers call ``persistAccount`` (using
    //      the captured ``accountId`` from the prompt, not ``editing``,
    //      so it works even if ``editing`` is cleared later)
    //   4. finally call reset() to clear form state
    if (editing) {
      const oldBal = Number(editing.balance || 0);
      const newBal = Number(v.balance);
      const delta = newBal - oldBal;
      if (Math.abs(delta) > 0.005) {
        setBalanceDeltaPrompt({
          payload,
          oldBalance: oldBal,
          newBalance: newBal,
          delta,
          ccy: payload.currency,
          accountId: editing.id,
        });
        setOpen(false);   // close BottomSheet so the delta dialog isn't hidden behind it
        return; // dialog will pick up the flow
      }
    }
    await persistAccount(payload, /* recordMovement */ null);
  }, {
    fieldMap: { name: "name", balance: "balance", institution: "institution" },
    onError: ({ fieldErrors, message }) => {
      if (!Object.keys(fieldErrors).length) {
        toast.error(message || (isEs ? "No se pudo guardar" : "Save failed"));
      }
    },
  });

  /**
   * Centralised persistence path so the form's direct path and the
   * "record delta" dialog can share the toast / cache invalidation
   * branches. When ``recordMovement`` is a non-null payload, the
   * account update SENDS THE OLD BALANCE BACK as a no-op so the
   * movement engine is the one that moves the balance (preserving
   * the audit trail). Sending undefined/0 instead would default
   * ``CashAccountIn.balance`` to zero and break the math.
   *
   * ``accountId`` defaults to ``editing?.id`` so the direct path keeps
   * working byte-identically. The delta-dialog flow passes the id
   * explicitly because by the time those handlers run we may have
   * already called reset()/closed the BottomSheet, clearing
   * ``editing``.
   */
  async function persistAccount(payload, recordMovement, accountId = editing?.id) {
    if (accountId) {
      // iter-188-fix-2 — When the caller asked us to record the delta
      // as a movement, the account UPDATE itself must be a no-op on
      // ``balance`` (otherwise the movement engine adds the delta on
      // TOP of a wrong base). The backend ``CashAccountIn`` defaults
      // ``balance: float = 0``, so a stripped balance is interpreted
      // as zero — that's how the regression "está reemplazando el
      // valor" happened. Solution: send back the OLD balance, so
      // ``update_one`` is a no-op for that field; the movement does
      // the actual ``+delta``.
      const accountPayload = recordMovement
        ? { ...payload, balance: recordMovement.oldBalance }
        : payload;
      await cashAccountsService.update(accountId, accountPayload);
      if (recordMovement) {
        const amount = Math.abs(recordMovement.delta);
        const type = recordMovement.delta > 0 ? "income" : "expense";
        const today = new Date().toISOString().slice(0, 10);
        const desc = isEs
          ? `Ajuste manual de saldo (${recordMovement.payload.name})`
          : (lang === "pt"
            ? `Ajuste manual de saldo (${recordMovement.payload.name})`
            : `Manual balance adjustment (${recordMovement.payload.name})`);
        try {
          await movementsService.create({
            type,
            amount,
            // iter-188-fix-4 — Tag with the account's currency so the
            // backend doesn't assume the delta is in profile currency
            // and double-convert when applying it to the account.
            original_currency: recordMovement.ccy,
            description: desc,
            date: today,
            account_id: accountId,
            tags: ["balance-adjustment"],
          });
        } catch (e) {
          toast.error(isEs ? "No se pudo registrar el movimiento" : "Failed to record movement");
        }
        qc.invalidateQueries({ queryKey: ["movements"] });
        qc.invalidateQueries({ queryKey: ["history"] });
      }
    } else {
      await cashAccountsService.create(payload);
    }
    qc.invalidateQueries({ queryKey: ["cash-accounts"] });
    qc.invalidateQueries({ queryKey: ["dashboard"] });
    toast.success(
      accountId
        ? (isEs ? "Cuenta actualizada" : "Account updated")
        : (isEs ? "Cuenta creada" : "Account created"),
    );
    reset();
  }

  const totalCashOriginalsByCcy = {};
  accountsWithFx.forEach((a) => {
    totalCashOriginalsByCcy[a._origCurrency] =
      (totalCashOriginalsByCcy[a._origCurrency] || 0) + Number(a.balance || 0);
  });

  return (
    <Layout>
      <div className="max-w-5xl mx-auto fade-up" data-testid="cash-accounts-page">
        <PageHeader
            eyebrow={tx(lang, "Liquid net worth", "Patrimonio líquido", "Patrimonio líquido")}
            title={tx(lang, "Accounts", "Cuentas", "Cuentas")}
            subtitle={tx(lang, "Split your cash across accounts (Nu, Bancolombia, cash…) and keep it visible.", "Distribuí tu cash entre cuentas (Nu, Bancolombia, efectivo…) y mantenelo claro.", "Distribuí tu cash entre cuentas (Nu, Bancolombia, efectivo…) y mantenelo claro.")}
            testid="accounts-header"
            resourceKey="cash_accounts"
            actions={
              <div className="flex items-center gap-2 flex-wrap">
                <TransferDialog
                  accounts={accounts}
                  isEs={lang === "es"}
                  trigger={
                    <Button
                      variant="outline"
                      disabled={accounts.length < 2}
                      className="gap-2 h-12 sm:h-10"
                      data-testid="open-transfer-dialog-btn"
                      title={accounts.length < 2
                        ? (tx(lang, "You need at least 2 accounts", "Necesitas al menos 2 cuentas", "Necesitas al menos 2 cuentas"))
                        : ""}
                    >
                      <ArrowRightLeft className="h-4 w-4" />
                      {tx(lang, "Transfer", "Trasladar", "Trasladar")}
                    </Button>
                  }
                />
                <CapAwareButton
                  resourceKey="cash_accounts"
                  featureLabel={tx(lang, "accounts", "cuentas", "contas")}
                  isEs={lang === "es"}
                  hideBadge
                  data-testid="add-cash-account-btn"
                  onClick={() => { reset(); setOpen(true); }}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white gap-2 h-12 sm:h-10 font-semibold"
                >
                  <Plus className="h-4 w-4" />
                  {tx(lang, "New account", "Nueva cuenta", "Nueva conta")}
                </CapAwareButton>
              </div>
            }
          />
        <BottomSheet
          open={open}
          onOpenChange={(v) => (v ? setOpen(true) : reset())}
          testid="cash-account-dialog"
          size="md"
          title={editing ? (tx(lang, "Edit account", "Editar cuenta", "Editar cuenta")) : (tx(lang, "New account", "Nueva cuenta", "Nueva cuenta"))}
          description={tx(lang, "Account details", "Datos de la cuenta", "Datos de la cuenta")}
          footer={
            <div className="flex items-center justify-end gap-2">
              <SubmitButton
                form="cash-account-form"
                submitting={form.submitting}
                testid="save-cash-account"
                className="bg-emerald-600 hover:bg-emerald-700 text-white h-11 px-5 font-semibold"
                loadingText={isEs ? "Guardando…" : "Saving…"}
              >
                <Save className="h-4 w-4" />
                <span>{isEs ? "Guardar" : "Save"}</span>
              </SubmitButton>
            </div>
          }
        >
              <form id="cash-account-form" onSubmit={onSubmit} className="space-y-3" noValidate>
                <FormField
                  label={isEs ? "Tipo" : "Type"}
                  name="type"
                  hint={
                    form.values.type === "cash"
                      ? (isEs
                          ? "Efectivo físico — no necesitamos institución."
                          : "Physical cash — no institution required.")
                      : form.values.type === "bank"
                        ? (isEs
                            ? "Cuenta bancaria (ahorros, corriente). Indica el banco."
                            : "Bank account (savings, checking). Add the bank name.")
                        : (isEs
                            ? "Billetera digital (Nu, Nequi, PayPal, Wise…)."
                            : "Digital wallet (Nu, Nequi, PayPal, Wise…).")
                  }
                  testid="form-field-cash-account-type"
                >
                  <Select
                    value={form.values.type}
                    onValueChange={(v) => form.setValue("type", v)}
                  >
                    <SelectTrigger data-testid="cash-account-type" className="h-12 rounded-xl"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="bank">{TYPE_LABELS.bank[isEs ? "es" : "en"]}</SelectItem>
                      <SelectItem value="cash">{TYPE_LABELS.cash[isEs ? "es" : "en"]}</SelectItem>
                      <SelectItem value="wallet">{TYPE_LABELS.wallet[isEs ? "es" : "en"]}</SelectItem>
                    </SelectContent>
                  </Select>
                </FormField>

                <FormField
                  label={isEs ? "Nombre" : "Name"}
                  name="name"
                  required
                  error={form.errors.name && (form.touched.name || form.submitCount > 0) ? form.errors.name : undefined}
                >
                  <FormField.Input
                    data-testid="cash-account-name"
                    autoComplete="off"
                    className="h-12 text-base rounded-xl"
                    placeholder={
                      form.values.type === "cash"
                        ? (isEs ? "Efectivo cartera" : "Wallet cash")
                        : form.values.type === "bank"
                          ? (isEs ? "Bancolombia ahorros" : "Chase checking")
                          : (isEs ? "Nu" : "Nu")
                    }
                    {...form.getFieldProps("name")}
                  />
                </FormField>

                {form.values.type !== "cash" && (
                  <FormField
                    label={form.values.type === "bank"
                      ? (isEs ? "Banco" : "Bank")
                      : (isEs ? "Proveedor" : "Provider")}
                    name="institution"
                    testid="cash-account-institution-wrap"
                    error={form.errors.institution && (form.touched.institution || form.submitCount > 0) ? form.errors.institution : undefined}
                  >
                    <FormField.Input
                      data-testid="cash-account-institution"
                      autoComplete="organization"
                      className="h-12 text-base rounded-xl"
                      placeholder={
                        form.values.type === "bank"
                          ? (isEs ? "Bancolombia, BBVA, Chase…" : "Chase, HSBC, Bancolombia…")
                          : (isEs ? "Nu, Nequi, PayPal, Wise…" : "Nu, Nequi, PayPal, Wise…")
                      }
                      {...form.getFieldProps("institution")}
                    />
                  </FormField>
                )}

                <div className="grid grid-cols-[1fr_120px] gap-3">
                  <FormField
                    label={isEs ? "Saldo actual" : "Current balance"}
                    name="balance"
                    required
                    error={form.errors.balance && (form.touched.balance || form.submitCount > 0) ? form.errors.balance : undefined}
                  >
                    <FormField.Currency
                      data-testid="cash-account-balance"
                      currency={form.values.currency || currency}
                      className="h-12 text-base rounded-xl"
                      value={form.values.balance}
                      onChange={(e) => form.setValue("balance", e.target.value)}
                      onBlur={() => form.markTouched("balance")}
                    />
                  </FormField>
                  <div>
                    <Label className="text-xs font-medium text-slate-700">{isEs ? "Moneda" : "Currency"}</Label>
                    <Select
                      value={form.values.currency || currency}
                      onValueChange={(v) => form.setValue("currency", v === currency ? "" : v)}
                    >
                      <SelectTrigger data-testid="cash-account-currency" className="h-12 mt-1.5 rounded-xl"><SelectValue /></SelectTrigger>
                      <SelectContent className="max-h-72">
                        {(fxList.length ? fxList : [currency]).map((c) => (
                          <SelectItem key={c} value={c}>{c}{c === currency ? ` · ${tx(lang, "primary", "principal", "principal")}` : ""}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                {form.values.currency && form.values.currency !== currency && (
                  <p className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-2 py-1.5" data-testid="cash-account-fx-note">
                    {tx(lang, `Balance in ${form.values.currency} is shown as-is; the dashboard total assumes ${currency}. Auto-conversion is on the roadmap.`, `El saldo en ${form.values.currency} se mostrará tal cual; el dashboard total asume ${currency}. Próximamente convertiremos automáticamente.`, `El saldo en ${form.values.currency} se mostrará tal cual; el dashboard total asume ${currency}. Próximamente convertiremos automáticamente.`)}
                  </p>
                )}
              </form>
            </BottomSheet>

        <div className="space-y-6">

        {/* Total */}
        <Card className="lf-surface lf-surface-hover p-6 bg-gradient-to-br from-emerald-50 to-white">
          <div className="flex items-baseline justify-between flex-wrap gap-2">
            <div>
              <div className="text-[11px] uppercase tracking-wider font-semibold text-slate-500">
                {tx(lang, "Total cash", "Total efectivo", "Total efectivo")}
              </div>
              <div className="font-display text-3xl font-bold text-slate-900 tabular-nums mt-1" data-testid="cash-total">
                ≈ {formatMoney(totalCash, currency)}
              </div>
              {/* Per-currency originals breakdown — helps users see why
                  the local-currency total moves when FX changes, without
                  silently swallowing the original amounts. */}
              {Object.keys(totalCashOriginalsByCcy).length > 1 && (
                <div className="text-[11px] text-slate-500 mt-1 tabular-nums" data-testid="cash-total-originals">
                  {Object.entries(totalCashOriginalsByCcy)
                    .filter(([ccy]) => ccy !== currency)
                    .map(([ccy, amt]) => `${formatMoney(amt, ccy)} ${ccy}`)
                    .join(" · ")}
                </div>
              )}
            </div>
            <div className="text-xs text-slate-500">
              {accounts.length} {tx(lang, "account(s)", "cuenta(s)", "cuenta(s)")}
            </div>
          </div>
        </Card>

        {/* Accounts */}
        <div className="space-y-3">
          {isLoading ? (
            <div className="text-sm text-slate-400 py-8 text-center">…</div>
          ) : accountsWithFx.length === 0 ? (
            <Card className="p-10 rounded-2xl border-dashed border-slate-200 text-center">
              <Wallet className="h-10 w-10 text-slate-300 mx-auto mb-3" />
              <p className="text-sm text-slate-500">
                {tx(lang, "No accounts yet. Create your first to split your cash.", "Aún no tienes cuentas. Crea la primera para distribuir tu cash.", "Aún no tienes cuentas. Crea la primera para distribuir tu cash.")}
              </p>
            </Card>
          ) : (
            accountsWithFx.map((a) => {
              const Icon = TYPE_ICONS[a.type] || Wallet;
              const isForeign = a._origCurrency !== currency;
              return (
                <Card key={a.id} className="p-5 rounded-2xl border-slate-200" data-testid={`cash-account-${a.id}`}>
                  <div className="flex items-center justify-between gap-3 flex-wrap">
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="h-10 w-10 rounded-2xl bg-slate-100 text-slate-700 flex items-center justify-center shrink-0">
                        <Icon className="h-5 w-5" />
                      </div>
                      <div className="min-w-0">
                        <div className="font-semibold text-slate-900 truncate">{a.name}</div>
                        <div className="text-xs text-slate-500 capitalize">
                          {TYPE_LABELS[a.type]?.[tx(lang, "en", "es", "es")] || a.type}
                          {a.institution ? ` · ${a.institution}` : ""}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="text-right" data-testid={`cash-account-balance-${a.id}`}>
                        {isForeign ? (
                          <MoneyAmount
                            amount={a._converted}
                            currency={currency}
                            original_amount={Number(a.balance || 0)}
                            original_currency={a._origCurrency}
                            fx_rate={a._rate}
                            size="md"
                            tone="default"
                            testid={`cash-account-money-${a.id}`}
                          />
                        ) : (
                          <div className="font-display text-lg font-bold tabular-nums">
                            {formatMoney(a.balance, a._origCurrency)}
                          </div>
                        )}
                      </div>
                      <Button
                        size="icon"
                        variant="ghost"
                        data-testid={`quick-movement-${a.id}`}
                        onClick={() => setQuickMovementAccount(a)}
                        title={tx(lang, "Add movement", "Nuevo movimiento", "Novo movimento")}
                        aria-label={tx(lang, "Add movement", "Nuevo movimiento", "Novo movimento")}
                      >
                        <PlusCircle className="h-4 w-4 text-emerald-600" />
                      </Button>
                      <Button
                        size="icon"
                        variant="ghost"
                        data-testid={`view-movements-${a.id}`}
                        onClick={() => navigate(`/history?account_id=${a.id}`)}
                        title={tx(lang, "View movements", "Ver movimientos", "Ver movimentos")}
                        aria-label={tx(lang, "View movements", "Ver movimientos", "Ver movimentos")}
                      >
                        <ListChecks className="h-4 w-4 text-slate-600" />
                      </Button>
                      <Button size="icon" variant="ghost" data-testid={`edit-cash-${a.id}`} onClick={() => openEdit(a)}>
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button size="icon" variant="ghost" data-testid={`delete-cash-${a.id}`} onClick={() => setDeleteTarget(a)}>
                        <Trash2 className="h-4 w-4 text-red-600" />
                      </Button>
                    </div>
                  </div>
                </Card>
              );
            })
          )}
        </div>
        </div>
      </div>

      <AlertDialog open={!!deleteTarget} onOpenChange={(v) => !v && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {tx(lang, "Delete account?", "¿Eliminar cuenta?", "¿Eliminar cuenta?")}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {tx(lang, `"${deleteTarget?.name}" will be removed. Movements stay intact.`, `Se eliminará "${deleteTarget?.name}". Los movimientos no se borran.`, `Se eliminará "${deleteTarget?.name}". Los movimientos no se borran.`)}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{tx(lang, "Cancel", "Cancelar", "Cancelar")}</AlertDialogCancel>
            <AlertDialogAction
              data-testid="confirm-delete-cash"
              onClick={() => deleteTarget && deleteMutation.mutate(deleteTarget.id)}
              className="bg-red-600 hover:bg-red-700"
            >
              {tx(lang, "Delete", "Eliminar", "Eliminar")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* iter-188 — Balance-adjustment intent dialog. Surfaces the
          delta in big tabular numbers + lets the user pick "record as
          movement" (preferred for the audit trail) or "just adjust". */}
      <Dialog open={!!balanceDeltaPrompt} onOpenChange={(v) => !v && setBalanceDeltaPrompt(null)}>
        <DialogContent data-testid="balance-delta-dialog" className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>
              {tx(lang, "Record balance change?", "¿Registrar el cambio de saldo?", "Registrar a mudança de saldo?")}
            </DialogTitle>
            <DialogDescription>
              {tx(lang,
                "Recording the difference as a movement keeps your history accurate. The new balance is reached through the movement, not a silent edit.",
                "Registrar la diferencia como movimiento mantiene tu historial preciso. El nuevo saldo se alcanza a través del movimiento, no con un ajuste invisible.",
                "Registrar a diferença como movimentação mantém seu histórico preciso. O novo saldo é alcançado pelo movimento, não por um ajuste invisível.")}
            </DialogDescription>
          </DialogHeader>
          {balanceDeltaPrompt && (
            <div className="mt-2 p-3 rounded-xl border border-slate-200 bg-slate-50 text-sm space-y-1.5">
              <div className="flex justify-between">
                <span className="text-slate-500">{tx(lang, "Previous balance", "Saldo anterior", "Saldo anterior")}</span>
                <span className="tabular-nums font-mono">{formatMoney(balanceDeltaPrompt.oldBalance, balanceDeltaPrompt.ccy)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-500">{tx(lang, "New balance", "Nuevo saldo", "Novo saldo")}</span>
                <span className="tabular-nums font-mono">{formatMoney(balanceDeltaPrompt.newBalance, balanceDeltaPrompt.ccy)}</span>
              </div>
              <div className="flex justify-between border-t border-slate-200 pt-1.5 mt-1.5">
                <span className="font-semibold">{tx(lang, "Difference", "Diferencia", "Diferença")}</span>
                <span
                  data-testid="balance-delta-amount"
                  className={`tabular-nums font-mono font-bold ${balanceDeltaPrompt.delta > 0 ? "text-emerald-600" : "text-red-600"}`}
                >
                  {balanceDeltaPrompt.delta > 0 ? "+" : ""}{formatMoney(balanceDeltaPrompt.delta, balanceDeltaPrompt.ccy)}
                </span>
              </div>
              <div className="text-xs text-slate-500 pt-1">
                {tx(lang,
                  `Will be saved as an ${balanceDeltaPrompt.delta > 0 ? "Income" : "Expense"}.`,
                  `Se guardará como ${balanceDeltaPrompt.delta > 0 ? "Ingreso" : "Gasto"}.`,
                  `Será salvo como ${balanceDeltaPrompt.delta > 0 ? "Receita" : "Despesa"}.`)}
              </div>
            </div>
          )}
          <DialogFooter className="gap-2 mt-3 flex-col sm:flex-row">
            <Button
              variant="outline"
              data-testid="balance-delta-just-adjust"
              onClick={async () => {
                const p = balanceDeltaPrompt;
                setBalanceDeltaPrompt(null);
                if (p) await persistAccount(p.payload, null, p.accountId);
              }}
            >
              {tx(lang, "Just adjust", "Solo ajustar", "Apenas ajustar")}
            </Button>
            <Button
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
              data-testid="balance-delta-record-movement"
              onClick={async () => {
                const p = balanceDeltaPrompt;
                setBalanceDeltaPrompt(null);
                if (p) await persistAccount(p.payload, p, p.accountId);
              }}
            >
              {balanceDeltaPrompt && balanceDeltaPrompt.delta > 0
                ? tx(lang, "Record as Income", "Registrar como Ingreso", "Registrar como Receita")
                : tx(lang, "Record as Expense", "Registrar como Gasto", "Registrar como Despesa")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <QuickMovementDialog
        account={quickMovementAccount}
        open={!!quickMovementAccount}
        onOpenChange={(v) => !v && setQuickMovementAccount(null)}
      />
    </Layout>
  );
}
